import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Shield, Database, AlertTriangle, CheckCircle2, XCircle,
  Clock, RefreshCw, ChevronRight, Filter, Download, Upload,
  Server, Cloud, Lock, Bug, FileText, Eye, Check, X, ExternalLink
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import api from '@/services/api';

const VulnerabilityDBAdminPage = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [updateHistory, setUpdateHistory] = useState([]);
  const [vulnerabilities, setVulnerabilities] = useState([]);
  const [misconfigurations, setMisconfigurations] = useState([]);
  const [sources, setSources] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedUpdate, setSelectedUpdate] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [triggeringUpdate, setTriggeringUpdate] = useState(false);
  const [syncingCSPM, setSyncingCSPM] = useState(false);
  // Prowler compliance + exceptions state
  const [complianceFrameworks, setComplianceFrameworks] = useState([]);
  const [complianceProviderFilter, setComplianceProviderFilter] = useState('all');
  const [syncingCompliance, setSyncingCompliance] = useState(false);
  const [prowlerStatus, setProwlerStatus] = useState(null);
  const [exceptionTemplates, setExceptionTemplates] = useState([]);
  const [syncingMutelist, setSyncingMutelist] = useState(false);
  // Cloud rules enhancements — filters, source picker, custom rule authoring
  const [misconfigSearch, setMisconfigSearch] = useState('');
  const [misconfigProviderFilter, setMisconfigProviderFilter] = useState('all');
  const [misconfigSeverityFilter, setMisconfigSeverityFilter] = useState('all');
  const [misconfigSourceFilter, setMisconfigSourceFilter] = useState('all');
  const [enabledSyncSources, setEnabledSyncSources] = useState({
    scoutsuite: true, cloudsploit: true, prowler: false, cis_benchmarks: false,
  });
  const [showRuleDialog, setShowRuleDialog] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleForm, setRuleForm] = useState({
    rule_id: '', title: '', description: '',
    severity: 'medium', category: 'custom', cloud_provider: 'aws',
    resource_type: '', remediation: '', references_text: '',
    framework_ids_text: '', is_active: true,
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, pendingRes, historyRes, vulnsRes, misconfigRes, sourcesRes, complianceRes, prowlerRes] = await Promise.all([
        api.get('/admin/vulnerability-db/stats'),
        api.get('/admin/vulnerability-db/updates/pending'),
        api.get('/admin/vulnerability-db/updates/history?limit=20'),
        api.get('/admin/vulnerability-db/vulnerabilities?limit=50'),
        api.get('/admin/vulnerability-db/misconfigurations?limit=50'),
        api.get('/admin/vulnerability-db/sources'),
        api.get('/admin/vulnerability-db/compliance-frameworks').catch(() => ({ data: { frameworks: [] } })),
        api.get('/admin/vulnerability-db/prowler/status').catch(() => ({ data: null })),
      ]);
      
      setStats(statsRes.data);
      setPendingUpdates(pendingRes.data.pending_updates || []);
      setUpdateHistory(historyRes.data.updates || []);
      setVulnerabilities(vulnsRes.data.vulnerabilities || []);
      setMisconfigurations(misconfigRes.data.rules || []);
      setSources(sourcesRes.data);
      setComplianceFrameworks(complianceRes.data.frameworks || []);
      setProwlerStatus(prowlerRes.data);
    } catch (err) {
      console.error('Failed to load data:', err);
      // If 403, might need to seed the database first
      if (err.response?.status === 403) {
        toast.error('Super admin access required');
      } else {
        toast.error('Failed to load vulnerability database');
      }
    } finally {
      setLoading(false);
    }
  };

  const syncProwlerCompliance = async () => {
    setSyncingCompliance(true);
    try {
      const res = await api.post('/admin/vulnerability-db/prowler/sync-compliance');
      if (res.data.success) {
        toast.success(`Imported ${res.data.imported} compliance frameworks (${res.data.new || 0} new, ${res.data.updated || 0} updated)`);
        loadData();
      } else {
        toast.error(res.data.message || 'Compliance sync failed');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Compliance sync failed');
    } finally {
      setSyncingCompliance(false);
    }
  };

  const syncProwlerMutelist = async () => {
    setSyncingMutelist(true);
    try {
      const res = await api.post('/admin/vulnerability-db/prowler/sync-mutelist');
      toast.success(`Imported ${res.data.imported || 0} Amidala exception templates`);
      // Refresh templates list for the Exceptions tab
      const t = await api.get('/amidala/exceptions/templates/prowler').catch(() => ({ data: { templates: [] } }));
      setExceptionTemplates(t.data.templates || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Mutelist sync failed');
    } finally {
      setSyncingMutelist(false);
    }
  };

  const loadExceptionTemplates = async () => {
    try {
      const t = await api.get('/amidala/exceptions/templates/prowler');
      setExceptionTemplates(t.data.templates || []);
    } catch (err) {
      // ignore
    }
  };

  const seedDatabase = async () => {
    try {
      await api.post('/admin/vulnerability-db/seed');
      toast.success('Database seeded successfully');
      loadData();
    } catch (err) {
      toast.error('Failed to seed database');
    }
  };

  const triggerUpdateCheck = async () => {
    setTriggeringUpdate(true);
    try {
      const res = await api.post('/admin/vulnerability-db/trigger-update-check');
      toast.success('Update check triggered - new update pending approval');
      loadData();
    } catch (err) {
      toast.error('Failed to trigger update check');
    } finally {
      setTriggeringUpdate(false);
    }
  };

  const viewUpdateDetails = async (updateId) => {
    try {
      const res = await api.get(`/admin/vulnerability-db/updates/${updateId}`);
      setSelectedUpdate(res.data);
    } catch (err) {
      toast.error('Failed to load update details');
    }
  };

  const approveUpdate = async (updateId) => {
    try {
      await api.post(`/admin/vulnerability-db/updates/${updateId}/approve`, {
        apply_to_all_tenants: true
      });
      toast.success('Update approved and applied successfully');
      setSelectedUpdate(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to approve update');
    }
  };

  const rejectUpdate = async (updateId) => {
    if (!rejectReason || rejectReason.length < 10) {
      toast.error('Please provide a rejection reason (at least 10 characters)');
      return;
    }
    try {
      await api.post(`/admin/vulnerability-db/updates/${updateId}/reject`, {
        reason: rejectReason
      });
      toast.success('Update rejected');
      setShowRejectDialog(false);
      setRejectReason('');
      setSelectedUpdate(null);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reject update');
    }
  };

  const syncCSPMRules = async () => {
    setSyncingCSPM(true);
    try {
      const sources = Object.entries(enabledSyncSources)
        .filter(([, v]) => v)
        .map(([k]) => k);
      if (sources.length === 0) {
        toast.error('Select at least one source to sync');
        setSyncingCSPM(false);
        return;
      }
      const res = await api.post('/admin/vulnerability-db/cspm/sync-rules', { sources });
      if (res.data.success) {
        toast.success(`Synced ${res.data.rules_fetched} rules from ${sources.join(', ')} - pending approval`);
        loadData();
      } else {
        toast.error(res.data.message || 'Failed to sync CSPM rules');
      }
    } catch (err) {
      toast.error('Failed to sync CSPM rules');
    } finally {
      setSyncingCSPM(false);
    }
  };

  const openCreateRule = () => {
    setEditingRule(null);
    setRuleForm({
      rule_id: '', title: '', description: '',
      severity: 'medium', category: 'custom', cloud_provider: 'aws',
      resource_type: '', remediation: '', references_text: '',
      framework_ids_text: '', is_active: true,
    });
    setShowRuleDialog(true);
  };

  const openEditRule = (rule) => {
    setEditingRule(rule);
    setRuleForm({
      rule_id: rule.rule_id || '',
      title: rule.title || '',
      description: rule.description || '',
      severity: rule.severity || 'medium',
      category: rule.category || 'custom',
      cloud_provider: rule.cloud_provider || 'aws',
      resource_type: rule.resource_type || '',
      remediation: rule.remediation || '',
      references_text: (rule.references || []).join('\n'),
      framework_ids_text: (rule.framework_mappings || []).map(m => `${m.Framework || ''}:${m.ControlId || ''}`).join(', '),
      is_active: rule.is_active !== false,
    });
    setShowRuleDialog(true);
  };

  const saveRule = async () => {
    const payload = {
      rule_id: ruleForm.rule_id.trim(),
      title: ruleForm.title.trim(),
      description: ruleForm.description.trim(),
      severity: ruleForm.severity,
      category: ruleForm.category,
      cloud_provider: ruleForm.cloud_provider,
      resource_type: ruleForm.resource_type,
      remediation: ruleForm.remediation,
      references: ruleForm.references_text.split('\n').map(s => s.trim()).filter(Boolean),
      framework_mappings: ruleForm.framework_ids_text.split(',').map(s => {
        const [fw, ctrl] = s.split(':');
        return { Framework: (fw || '').trim(), ControlId: (ctrl || '').trim() };
      }).filter(m => m.Framework),
      is_active: ruleForm.is_active,
    };
    if (!payload.rule_id || !payload.title) {
      toast.error('rule_id and title are required');
      return;
    }
    try {
      if (editingRule) {
        await api.put(`/admin/vulnerability-db/misconfigurations/${payload.rule_id}`, payload);
        toast.success('Rule updated');
      } else {
        await api.post('/admin/vulnerability-db/misconfigurations', payload);
        toast.success('Rule created');
      }
      setShowRuleDialog(false);
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save rule');
    }
  };

  const deleteRule = async (rule_id) => {
    if (!window.confirm(`Delete rule ${rule_id}?`)) return;
    try {
      await api.delete(`/admin/vulnerability-db/misconfigurations/${rule_id}`);
      toast.success('Rule deleted');
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete rule');
    }
  };

  const filteredMisconfigurations = misconfigurations.filter((r) => {
    if (misconfigProviderFilter !== 'all' && r.cloud_provider !== misconfigProviderFilter) return false;
    if (misconfigSeverityFilter !== 'all' && r.severity !== misconfigSeverityFilter) return false;
    if (misconfigSourceFilter !== 'all' && (r.source || 'unknown') !== misconfigSourceFilter) return false;
    if (misconfigSearch) {
      const q = misconfigSearch.toLowerCase();
      const hay = `${r.rule_id} ${r.title} ${r.description} ${r.category}`.toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  const [syncingNVD, setSyncingNVD] = useState(false);
  const [nvdCves, setNvdCves] = useState([]);
  const [nvdSearch, setNvdSearch] = useState('');
  const [autoSync, setAutoSync] = useState({ nvd_enabled: true, nvd_days: 7, nvd_max_cves: 2000, cspm_enabled: true, cspm_sources: null });
  const [autoSyncState, setAutoSyncState] = useState([]);
  const [autoSyncSaving, setAutoSyncSaving] = useState(false);
  const [autoSyncRunning, setAutoSyncRunning] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [settingsRes, stateRes] = await Promise.all([
          api.get('/admin/vulnerability-db/auto-sync/settings').catch(() => ({ data: null })),
          api.get('/admin/vulnerability-db/auto-sync/state').catch(() => ({ data: { states: [] } })),
        ]);
        if (settingsRes.data) setAutoSync((prev) => ({ ...prev, ...settingsRes.data }));
        if (stateRes.data?.states) setAutoSyncState(stateRes.data.states);
      } catch (_e) { /* ignore — auto-sync panel is best-effort */ }
    })();
  }, []);

  const saveAutoSync = async (next) => {
    setAutoSyncSaving(true);
    try {
      const payload = { ...autoSync, ...next };
      const res = await api.put('/admin/vulnerability-db/auto-sync/settings', payload);
      setAutoSync(res.data);
      toast.success('Auto-sync settings saved');
    } catch (err) {
      toast.error('Failed to save auto-sync settings');
    } finally { setAutoSyncSaving(false); }
  };

  const runAutoSyncNow = async () => {
    setAutoSyncRunning(true);
    try {
      const res = await api.post('/admin/vulnerability-db/auto-sync/run-now');
      setAutoSyncState(res.data.states || []);
      toast.success('Upstream sync triggered — new CVEs + rules are being pulled');
      loadData();
    } catch (err) {
      toast.error('Failed to trigger auto-sync');
    } finally { setAutoSyncRunning(false); }
  };

  const syncNVD = async () => {
    setSyncingNVD(true);
    try {
      // Paginate through the full 30-day window (up to 2000 CVEs) instead of
      // stopping at 20 — the backend now walks NVD via startIndex and dedups.
      const res = await api.post('/threat-intel/nvd/sync?days=30&max_cves=2000&page_size=200');
      const s = res.data || {};
      const msg = `NVD sync complete — ${s.synced || 0} new, ${s.updated || 0} updated (${s.total_fetched || 0} scanned across ${s.pages_fetched || 0} pages)`;
      toast.success(msg);
      loadData();
    } catch (err) {
      toast.error('Failed to sync NVD data');
    } finally { setSyncingNVD(false); }
  };

  const searchNVD = async () => {
    try {
      const params = nvdSearch ? `keyword=${encodeURIComponent(nvdSearch)}&limit=20` : 'days=7&limit=20';
      const res = await api.get(`/threat-intel/nvd/cves?${params}`);
      setNvdCves(res.data.vulnerabilities || []);
      toast.success(`Found ${res.data.total_results} CVEs`);
    } catch { toast.error('NVD search failed'); }
  };

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-600 text-white';
      case 'high': return 'bg-orange-500 text-white';
      case 'medium': return 'bg-yellow-500 text-black';
      case 'low': return 'bg-blue-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'applied': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getUpdateTypeIcon = (type) => {
    switch (type) {
      case 'vulnerability': return <Bug className="w-4 h-4" />;
      case 'misconfiguration': return <Cloud className="w-4 h-4" />;
      case 'security_issue': return <AlertTriangle className="w-4 h-4" />;
      case 'cspm_rule': return <Server className="w-4 h-4" />;
      default: return <Shield className="w-4 h-4" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="vuln-db-admin-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Database className="w-7 h-7 text-indigo-600" />
            Vulnerability Database Management
          </h1>
          <p className="text-gray-500 mt-1">
            Manage vulnerability updates, misconfiguration rules, and security advisories
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={seedDatabase}>
            <Upload className="w-4 h-4 mr-2" />
            Seed Database
          </Button>
          <Button 
            onClick={triggerUpdateCheck}
            disabled={triggeringUpdate}
            data-testid="trigger-update-btn"
          >
            {triggeringUpdate ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Check for Updates
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <Bug className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_vulnerabilities || 0}</p>
                <p className="text-xs text-gray-500">Vulnerabilities</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Cloud className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_misconfiguration_rules || 0}</p>
                <p className="text-xs text-gray-500">Misconfig Rules</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-yellow-100 flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.total_security_issues || 0}</p>
                <p className="text-xs text-gray-500">Security Issues</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card className={pendingUpdates.length > 0 ? 'border-yellow-400 bg-yellow-50' : ''}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className={`w-10 h-10 rounded-lg ${pendingUpdates.length > 0 ? 'bg-yellow-200' : 'bg-gray-100'} flex items-center justify-center`}>
                <Clock className={`w-5 h-5 ${pendingUpdates.length > 0 ? 'text-yellow-700' : 'text-gray-600'}`} />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats?.pending_updates || 0}</p>
                <p className="text-xs text-gray-500">Pending Approval</p>
              </div>
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium truncate">
                  {stats?.last_update ? new Date(stats.last_update).toLocaleDateString() : 'Never'}
                </p>
                <p className="text-xs text-gray-500">Last Update</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Pending Updates Alert */}
      {pendingUpdates.length > 0 && (
        <Card className="border-yellow-400 bg-yellow-50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2 text-yellow-800">
              <AlertTriangle className="w-5 h-5" />
              {pendingUpdates.length} Update(s) Pending Approval
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {pendingUpdates.map((update) => (
                <div 
                  key={update.id}
                  className="flex items-center justify-between p-3 bg-white rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    {getUpdateTypeIcon(update.update_type)}
                    <div>
                      <p className="font-medium">{update.title}</p>
                      <p className="text-sm text-gray-500">
                        {update.records_count} records • {update.new_records} new, {update.updated_records} updated
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-400">
                      {new Date(update.created_at).toLocaleString()}
                    </span>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => viewUpdateDetails(update.id)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Review
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main Content Tabs */}
      <Tabs defaultValue="vulnerabilities" className="w-full">
        <TabsList>
          <TabsTrigger value="vulnerabilities">
            <Bug className="w-4 h-4 mr-2" />
            Vulnerabilities ({vulnerabilities.length})
          </TabsTrigger>
          <TabsTrigger value="misconfigurations">
            <Cloud className="w-4 h-4 mr-2" />
            Misconfiguration Rules ({misconfigurations.length})
          </TabsTrigger>
          <TabsTrigger value="compliance" data-testid="tab-compliance">
            <Shield className="w-4 h-4 mr-2" />
            Compliance ({complianceFrameworks.length})
          </TabsTrigger>
          <TabsTrigger value="exceptions" data-testid="tab-exceptions">
            <XCircle className="w-4 h-4 mr-2" />
            Exceptions
          </TabsTrigger>
          <TabsTrigger value="prowler" data-testid="tab-prowler">
            <Server className="w-4 h-4 mr-2" />
            Amidala Rules Lib
          </TabsTrigger>
          <TabsTrigger value="sources">
            <Server className="w-4 h-4 mr-2" />
            Data Sources
          </TabsTrigger>
          <TabsTrigger value="nvd">
            <Shield className="w-4 h-4 mr-2" />
            NVD (NIST)
          </TabsTrigger>
          <TabsTrigger value="history">
            <FileText className="w-4 h-4 mr-2" />
            Update History
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vulnerabilities" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Vulnerability Database</CardTitle>
                <Input 
                  placeholder="Search by CVE or package..."
                  className="w-64"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium">CVE ID</th>
                      <th className="text-left p-3 font-medium">Package</th>
                      <th className="text-center p-3 font-medium">Severity</th>
                      <th className="text-left p-3 font-medium">Title</th>
                      <th className="text-left p-3 font-medium">Fixed In</th>
                      <th className="text-left p-3 font-medium">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {vulnerabilities
                      .filter(v => 
                        !searchTerm || 
                        v.cve_id?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                        v.package_name?.toLowerCase().includes(searchTerm.toLowerCase())
                      )
                      .map((vuln, idx) => (
                        <tr key={vuln.id || idx} className="border-b hover:bg-gray-50">
                          <td className="p-3">
                            <a 
                              href={`https://nvd.nist.gov/vuln/detail/${vuln.cve_id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:underline font-mono text-sm"
                            >
                              {vuln.cve_id}
                            </a>
                          </td>
                          <td className="p-3">
                            <span className="font-medium">{vuln.package_name}</span>
                            <span className="text-gray-400 text-xs ml-1">({vuln.ecosystem})</span>
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={getSeverityColor(vuln.severity)}>
                              {vuln.severity?.toUpperCase()}
                            </Badge>
                          </td>
                          <td className="p-3 max-w-xs truncate">{vuln.title}</td>
                          <td className="p-3">
                            {vuln.fixed_version ? (
                              <span className="text-green-600 font-mono">{vuln.fixed_version}</span>
                            ) : (
                              <span className="text-red-500">No fix</span>
                            )}
                          </td>
                          <td className="p-3 text-gray-500 text-xs">{vuln.source}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {vulnerabilities.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No vulnerabilities in database. Click "Seed Database" to add initial data.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="misconfigurations" className="mt-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Cloud Security Rules ({filteredMisconfigurations.length} / {misconfigurations.length})</CardTitle>
                  <CardDescription>Browse, sync, and author misconfiguration detection rules for CSPM</CardDescription>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" data-testid="add-custom-rule-btn" onClick={openCreateRule}>
                    + Add Custom Rule
                  </Button>
                  <Button variant="outline" size="sm" onClick={syncCSPMRules} disabled={syncingCSPM} data-testid="sync-cspm-btn">
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncingCSPM ? 'animate-spin' : ''}`} />
                    {syncingCSPM ? 'Syncing...' : 'Sync Selected Sources'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {/* Source picker */}
              <div className="mb-4 p-3 border rounded-lg bg-gray-50" data-testid="sync-sources-picker">
                <div className="text-xs font-semibold text-gray-700 mb-2">Upstream Rule Packs</div>
                <div className="flex flex-wrap gap-3">
                  {[
                    { key: 'scoutsuite', label: 'ScoutSuite' },
                    { key: 'cloudsploit', label: 'CloudSploit' },
                    { key: 'prowler', label: 'Prowler' },
                    { key: 'cis_benchmarks', label: 'CIS Benchmarks' },
                  ].map(s => (
                    <label key={s.key} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        data-testid={`sync-source-${s.key}`}
                        checked={enabledSyncSources[s.key]}
                        onChange={(e) => setEnabledSyncSources(prev => ({ ...prev, [s.key]: e.target.checked }))}
                        className="rounded"
                      />
                      {s.label}
                    </label>
                  ))}
                </div>
              </div>

              {/* Filters */}
              <div className="mb-4 flex flex-wrap gap-2">
                <Input
                  data-testid="misconfig-search"
                  placeholder="Search rules by ID, title, description..."
                  value={misconfigSearch}
                  onChange={(e) => setMisconfigSearch(e.target.value)}
                  className="max-w-md"
                />
                <select
                  data-testid="misconfig-provider-filter"
                  value={misconfigProviderFilter}
                  onChange={(e) => setMisconfigProviderFilter(e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value="all">All Providers</option>
                  <option value="aws">AWS</option>
                  <option value="azure">Azure</option>
                  <option value="gcp">GCP</option>
                </select>
                <select
                  data-testid="misconfig-severity-filter"
                  value={misconfigSeverityFilter}
                  onChange={(e) => setMisconfigSeverityFilter(e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value="all">All Severities</option>
                  <option value="critical">Critical</option>
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
                <select
                  data-testid="misconfig-source-filter"
                  value={misconfigSourceFilter}
                  onChange={(e) => setMisconfigSourceFilter(e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value="all">All Sources</option>
                  <option value="scoutsuite">ScoutSuite</option>
                  <option value="cloudsploit">CloudSploit</option>
                  <option value="prowler">Prowler</option>
                  <option value="cis_benchmarks">CIS Benchmarks</option>
                  <option value="custom">Custom</option>
                </select>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium">Rule ID</th>
                      <th className="text-left p-3 font-medium">Title</th>
                      <th className="text-center p-3 font-medium">Severity</th>
                      <th className="text-left p-3 font-medium">Category</th>
                      <th className="text-left p-3 font-medium">Provider</th>
                      <th className="text-left p-3 font-medium">Source</th>
                      <th className="text-left p-3 font-medium">Frameworks</th>
                      <th className="text-center p-3 font-medium">Status</th>
                      <th className="text-right p-3 font-medium">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredMisconfigurations.map((rule, idx) => (
                      <tr key={rule.id || rule.rule_id || idx} className="border-b hover:bg-gray-50">
                        <td className="p-3 font-mono text-xs">{rule.rule_id}</td>
                        <td className="p-3 font-medium">{rule.title}</td>
                        <td className="p-3 text-center">
                          <Badge className={getSeverityColor(rule.severity)}>
                            {rule.severity?.toUpperCase()}
                          </Badge>
                        </td>
                        <td className="p-3 text-gray-600">{rule.category}</td>
                        <td className="p-3">
                          <Badge variant="outline">{rule.cloud_provider?.toUpperCase()}</Badge>
                        </td>
                        <td className="p-3 text-xs text-gray-500 uppercase">{rule.source || '—'}</td>
                        <td className="p-3">
                          <div className="flex flex-wrap gap-1">
                            {(rule.framework_mappings || []).slice(0, 3).map((m, i) => (
                              <Badge key={i} variant="outline" className="text-[10px]">
                                {m.Framework}{m.ControlId ? `:${m.ControlId}` : ''}
                              </Badge>
                            ))}
                            {(rule.framework_mappings || []).length === 0 && (
                              <span className="text-xs text-gray-400">—</span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          {rule.is_active ? (
                            <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                          ) : (
                            <XCircle className="w-5 h-5 text-gray-400 mx-auto" />
                          )}
                        </td>
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button size="sm" variant="ghost" data-testid={`edit-rule-${rule.rule_id}`} onClick={() => openEditRule(rule)}>Edit</Button>
                            <Button size="sm" variant="ghost" className="text-red-600" data-testid={`delete-rule-${rule.rule_id}`} onClick={() => deleteRule(rule.rule_id)}>Del</Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredMisconfigurations.length === 0 && (
                  <div className="text-center py-8 text-gray-500" data-testid="misconfig-empty">
                    {misconfigurations.length === 0
                      ? 'No misconfiguration rules. Click "Seed Database" to add initial rules or "Add Custom Rule" to author your own.'
                      : 'No rules match the selected filters.'}
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Compliance Frameworks — Amidala Rules Library */}
        <TabsContent value="compliance" className="mt-4" data-testid="compliance-tab-content">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Compliance Frameworks ({complianceFrameworks.length})</CardTitle>
                  <CardDescription>
                    Amidala compliance rules library — imported mappings across aws / azure / gcp / kubernetes / m365.
                    Amidala CSPM scans compute coverage against these frameworks.
                  </CardDescription>
                </div>
                <Button onClick={syncProwlerCompliance} disabled={syncingCompliance} data-testid="sync-compliance-btn">
                  <RefreshCw className={`w-4 h-4 mr-2 ${syncingCompliance ? 'animate-spin' : ''}`} />
                  {syncingCompliance ? 'Syncing...' : 'Sync Amidala Compliance'}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="mb-3 flex gap-2">
                <select
                  data-testid="compliance-provider-filter"
                  value={complianceProviderFilter}
                  onChange={(e) => setComplianceProviderFilter(e.target.value)}
                  className="px-2 py-1 border rounded text-sm"
                >
                  <option value="all">All Providers</option>
                  <option value="aws">AWS</option>
                  <option value="azure">Azure</option>
                  <option value="gcp">GCP</option>
                  <option value="kubernetes">Kubernetes</option>
                  <option value="m365">M365</option>
                </select>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-gray-50">
                      <th className="text-left p-3 font-medium">Provider</th>
                      <th className="text-left p-3 font-medium">Framework</th>
                      <th className="text-left p-3 font-medium">Version</th>
                      <th className="text-center p-3 font-medium">Requirements</th>
                      <th className="text-left p-3 font-medium">File</th>
                      <th className="text-right p-3 font-medium">Source</th>
                    </tr>
                  </thead>
                  <tbody>
                    {complianceFrameworks
                      .filter(f => complianceProviderFilter === 'all' || f.provider === complianceProviderFilter)
                      .map((fw) => (
                        <tr key={`${fw.provider}-${fw.file_name}`} className="border-b hover:bg-gray-50" data-testid={`compliance-row-${fw.file_name}`}>
                          <td className="p-3">
                            <Badge variant="outline">{fw.provider?.toUpperCase()}</Badge>
                          </td>
                          <td className="p-3 font-medium">{fw.framework}</td>
                          <td className="p-3 text-gray-600">{fw.version || '—'}</td>
                          <td className="p-3 text-center">{fw.requirements_count}</td>
                          <td className="p-3 font-mono text-xs text-gray-500">{fw.file_name}</td>
                          <td className="p-3 text-right">
                            <a href={fw.source_url} target="_blank" rel="noopener noreferrer" className="text-indigo-600 hover:text-indigo-800 text-xs">
                              GitHub ↗
                            </a>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
                {complianceFrameworks.length === 0 && (
                  <div className="text-center py-8 text-gray-500" data-testid="compliance-empty">
                    No compliance frameworks yet. Click &quot;Sync Amidala Compliance&quot; to import ~46 frameworks.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Exceptions / Mutelist Templates */}
        <TabsContent value="exceptions" className="mt-4" data-testid="exceptions-tab-content">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Amidala Rule Exceptions & Mutelist Templates</CardTitle>
                  <CardDescription>
                    Import Amidala&apos;s default exception templates. Per-tenant rule exceptions are managed
                    from Amidala CSPM (Tenant Settings → Exceptions).
                  </CardDescription>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={loadExceptionTemplates} data-testid="load-exception-templates-btn">
                    Reload Templates
                  </Button>
                  <Button onClick={syncProwlerMutelist} disabled={syncingMutelist} data-testid="sync-mutelist-btn">
                    <RefreshCw className={`w-4 h-4 mr-2 ${syncingMutelist ? 'animate-spin' : ''}`} />
                    {syncingMutelist ? 'Syncing...' : 'Sync Amidala Exception Templates'}
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {exceptionTemplates.length === 0 ? (
                <div className="text-center py-8 text-gray-500" data-testid="exception-templates-empty">
                  No exception templates loaded. Click &quot;Sync Amidala Exception Templates&quot; then &quot;Reload Templates&quot;.
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-gray-50">
                        <th className="text-left p-3 font-medium">Account</th>
                        <th className="text-left p-3 font-medium">Check ID</th>
                        <th className="text-left p-3 font-medium">Region</th>
                        <th className="text-left p-3 font-medium">Resource Pattern</th>
                        <th className="text-left p-3 font-medium">Reason</th>
                      </tr>
                    </thead>
                    <tbody>
                      {exceptionTemplates.slice(0, 100).map((t) => (
                        <tr key={t.id} className="border-b hover:bg-gray-50">
                          <td className="p-3 font-mono text-xs">{t.account}</td>
                          <td className="p-3 font-mono text-xs">{t.check_id}</td>
                          <td className="p-3">{t.region}</td>
                          <td className="p-3 font-mono text-xs">{t.resource}</td>
                          <td className="p-3 text-gray-600">{t.reason}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {exceptionTemplates.length > 100 && (
                    <div className="text-xs text-gray-500 mt-2">Showing first 100 of {exceptionTemplates.length}</div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Amidala Rules Library Metadata */}
        <TabsContent value="prowler" className="mt-4" data-testid="prowler-tab-content">
          <Card>
            <CardHeader>
              <CardTitle>Amidala Rules Library Status</CardTitle>
              <CardDescription>Rule sync source, last synced commit, and imported artifact counts.</CardDescription>
            </CardHeader>
            <CardContent>
              {prowlerStatus ? (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-4 bg-gray-50 rounded-lg" data-testid="prowler-lib-repo">
                      <div className="text-xs text-gray-500 mb-1">Repository</div>
                      <div className="font-mono text-sm">{prowlerStatus.lib?.repo || '—'}</div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg" data-testid="prowler-lib-branch">
                      <div className="text-xs text-gray-500 mb-1">Branch</div>
                      <div className="font-mono text-sm">{prowlerStatus.lib?.branch || '—'}</div>
                    </div>
                    <div className="p-4 bg-gray-50 rounded-lg col-span-2" data-testid="prowler-lib-fetched-at">
                      <div className="text-xs text-gray-500 mb-1">Last Sync</div>
                      <div className="font-mono text-sm">{prowlerStatus.lib?.fetched_at || '—'}</div>
                      {prowlerStatus.lib?.commit_sha && (
                        <div className="mt-1 font-mono text-xs text-gray-600">
                          commit: {prowlerStatus.lib.commit_sha} — {prowlerStatus.lib.commit_message}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-4 gap-4">
                    <div className="p-4 bg-indigo-50 rounded-lg text-center" data-testid="prowler-count-compliance-frameworks">
                      <div className="text-xs text-gray-500 mb-1">Compliance Frameworks</div>
                      <div className="text-2xl font-bold text-indigo-700">{prowlerStatus.counts?.compliance_frameworks ?? 0}</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center" data-testid="prowler-count-prowler-rules">
                      <div className="text-xs text-gray-500 mb-1">Prowler Rules</div>
                      <div className="text-2xl font-bold text-green-700">{prowlerStatus.counts?.prowler_rules ?? 0}</div>
                    </div>
                    <div className="p-4 bg-amber-50 rounded-lg text-center" data-testid="prowler-count-exception-templates">
                      <div className="text-xs text-gray-500 mb-1">Exception Templates</div>
                      <div className="text-2xl font-bold text-amber-700">{prowlerStatus.counts?.exception_templates ?? 0}</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center" data-testid="prowler-count-amidala-exceptions">
                      <div className="text-xs text-gray-500 mb-1">Tenant Exceptions</div>
                      <div className="text-2xl font-bold text-purple-700">{prowlerStatus.counts?.amidala_exceptions ?? 0}</div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">Loading Prowler status…</div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="sources" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Vulnerability Sources */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Bug className="w-5 h-5 text-red-500" />
                  Vulnerability Data Sources
                </CardTitle>
                <CardDescription>
                  Real-time vulnerability data from multiple security databases
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sources?.vulnerability_sources?.map((source, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{source.name}</h4>
                          <p className="text-sm text-gray-500 mt-1">{source.description}</p>
                          <a 
                            href={source.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 hover:underline mt-1 inline-block"
                          >
                            {source.url}
                          </a>
                        </div>
                        <Badge className={source.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {source.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {source.ecosystems?.map((eco, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{eco}</Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Misconfiguration Sources */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Cloud className="w-5 h-5 text-orange-500" />
                  Cloud Security Rule Sources
                </CardTitle>
                <CardDescription>
                  Misconfiguration detection rules from industry-leading tools
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {sources?.misconfiguration_sources?.map((source, idx) => (
                    <div key={idx} className="p-4 border rounded-lg hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <h4 className="font-medium text-gray-900">{source.name}</h4>
                          <p className="text-sm text-gray-500 mt-1">{source.description}</p>
                          <a 
                            href={source.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-xs text-indigo-600 hover:underline mt-1 inline-block"
                          >
                            {source.url}
                          </a>
                        </div>
                        <Badge className={source.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                          {source.status}
                        </Badge>
                      </div>
                      <div className="flex flex-wrap gap-1 mt-3">
                        {source.cloud_providers?.map((cp, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{cp.toUpperCase()}</Badge>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <Button 
                    onClick={syncCSPMRules}
                    disabled={syncingCSPM}
                    className="w-full"
                  >
                    {syncingCSPM ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="w-4 h-4 mr-2" />
                    )}
                    Sync Cloud Security Rules
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* NVD (NIST) Tab */}
        <TabsContent value="nvd" className="mt-4 space-y-4">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    NVD - National Vulnerability Database (NIST)
                  </CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">
                    Search and sync CVEs from NIST's official vulnerability database
                  </p>
                </div>
                <Button onClick={syncNVD} disabled={syncingNVD} data-testid="sync-nvd-btn">
                  {syncingNVD ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                  Sync to Local DB
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              {/* Auto-Sync Automation Panel */}
              <div className="rounded-lg border border-indigo-100 bg-indigo-50/40 p-4 space-y-3" data-testid="auto-sync-panel">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <div className="flex items-center gap-2 text-sm font-semibold text-indigo-900">
                      <RefreshCw className="w-4 h-4" />
                      Automated Upstream Sync
                    </div>
                    <p className="text-xs text-indigo-800/70 mt-0.5">
                      Pulls fresh CVEs from NVD and misconfiguration rules from Prowler / ScoutSuite / CloudSploit / CIS every 24 hours. Amidala scans automatically pick up the new data.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={runAutoSyncNow}
                    disabled={autoSyncRunning}
                    data-testid="auto-sync-run-now-btn"
                  >
                    {autoSyncRunning ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <RefreshCw className="w-3 h-3 mr-1" />}
                    Run All Now
                  </Button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                  <label className="flex items-center justify-between gap-3 bg-white rounded border border-indigo-100 px-3 py-2 cursor-pointer">
                    <div>
                      <div className="font-medium">NVD CVEs</div>
                      <div className="text-xs text-gray-500">Auto-pull vulnerabilities every 24h</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={autoSync.nvd_enabled}
                      disabled={autoSyncSaving}
                      onChange={(e) => saveAutoSync({ nvd_enabled: e.target.checked })}
                      data-testid="auto-sync-nvd-toggle"
                    />
                  </label>
                  <label className="flex items-center justify-between gap-3 bg-white rounded border border-indigo-100 px-3 py-2 cursor-pointer">
                    <div>
                      <div className="font-medium">Upstream CSPM Rule Packs</div>
                      <div className="text-xs text-gray-500">Prowler / ScoutSuite / CloudSploit / CIS every 24h</div>
                    </div>
                    <input
                      type="checkbox"
                      checked={autoSync.cspm_enabled}
                      disabled={autoSyncSaving}
                      onChange={(e) => saveAutoSync({ cspm_enabled: e.target.checked })}
                      data-testid="auto-sync-cspm-toggle"
                    />
                  </label>
                </div>
                {autoSyncState.length > 0 && (
                  <div className="text-xs text-indigo-900/80 space-y-1" data-testid="auto-sync-state">
                    {autoSyncState.map((s) => (
                      <div key={s.source} className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded bg-white/70 font-mono">{s.source}</span>
                        <span>last sync {s.last_sync_at ? new Date(s.last_sync_at).toLocaleString() : 'never'}</span>
                        {typeof s.last_synced === 'number' && <span>· new: {s.last_synced}</span>}
                        {typeof s.last_updated === 'number' && <span>· updated: {s.last_updated}</span>}
                        {typeof s.last_inserted === 'number' && <span>· inserted: {s.last_inserted}</span>}
                        {typeof s.last_total_fetched === 'number' && <span>· fetched: {s.last_total_fetched}</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Input
                  placeholder="Search NVD by keyword or CVE ID (e.g., CVE-2024-1234, log4j, openssl)..."
                  value={nvdSearch}
                  onChange={(e) => setNvdSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && searchNVD()}
                  className="flex-1"
                  data-testid="nvd-search-input"
                />
                <Button onClick={searchNVD} data-testid="nvd-search-btn">Search NVD</Button>
              </div>
              {nvdCves.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left p-3 font-medium">CVE ID</th>
                        <th className="text-center p-3 font-medium">Severity</th>
                        <th className="text-center p-3 font-medium">CVSS</th>
                        <th className="text-left p-3 font-medium">Description</th>
                        <th className="text-left p-3 font-medium">Published</th>
                      </tr>
                    </thead>
                    <tbody>
                      {nvdCves.map((cve) => (
                        <tr key={cve.cve_id} className="border-t hover:bg-muted/30" data-testid={`nvd-cve-${cve.cve_id}`}>
                          <td className="p-3">
                            <a href={`https://nvd.nist.gov/vuln/detail/${cve.cve_id}`} target="_blank" rel="noopener noreferrer"
                               className="text-blue-600 hover:underline font-mono text-xs">
                              {cve.cve_id}
                            </a>
                          </td>
                          <td className="p-3 text-center">
                            <Badge className={getSeverityColor(cve.severity)}>{cve.severity}</Badge>
                          </td>
                          <td className="p-3 text-center font-bold">{cve.base_score}</td>
                          <td className="p-3 text-xs max-w-md truncate">{cve.description}</td>
                          <td className="p-3 text-xs text-muted-foreground">
                            {cve.published ? new Date(cve.published).toLocaleDateString() : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              {nvdCves.length === 0 && (
                <div className="text-center py-8 text-muted-foreground">
                  <Shield className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p>Search the NVD database or click "Sync to Local DB" to import recent CVEs</p>
                  <p className="text-xs mt-1">Data sourced from: <a href="https://nvd.nist.gov" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">https://nvd.nist.gov</a></p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Update History</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {updateHistory.map((update, idx) => (
                  <div 
                    key={update.id || idx}
                    className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                        update.status === 'applied' ? 'bg-green-100' :
                        update.status === 'rejected' ? 'bg-red-100' :
                        'bg-yellow-100'
                      }`}>
                        {getUpdateTypeIcon(update.update_type)}
                      </div>
                      <div>
                        <p className="font-medium">{update.title}</p>
                        <p className="text-sm text-gray-500">
                          {update.records_count} records • Created {new Date(update.created_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <Badge className={getStatusColor(update.status)}>
                        {update.status?.toUpperCase()}
                      </Badge>
                      {update.approved_by && (
                        <span className="text-xs text-gray-400">
                          by {update.approved_by}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {updateHistory.length === 0 && (
                  <div className="text-center py-8 text-gray-500">
                    No update history yet.
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Custom Rule Authoring Dialog */}
      <Dialog open={showRuleDialog} onOpenChange={setShowRuleDialog}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="rule-authoring-dialog">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Rule' : 'Add Custom Cloud Security Rule'}</DialogTitle>
            <DialogDescription>
              Author a misconfiguration detection rule. Rules created here are marked source=custom.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 mt-2">
            <div>
              <label className="text-xs font-medium text-gray-700">Rule ID *</label>
              <Input
                data-testid="rule-form-rule-id"
                value={ruleForm.rule_id}
                onChange={(e) => setRuleForm(v => ({ ...v, rule_id: e.target.value }))}
                placeholder="CUSTOM-AWS-S3-001"
                disabled={!!editingRule}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Severity</label>
              <select
                data-testid="rule-form-severity"
                className="w-full border rounded px-2 py-2 text-sm"
                value={ruleForm.severity}
                onChange={(e) => setRuleForm(v => ({ ...v, severity: e.target.value }))}
              >
                <option value="critical">Critical</option>
                <option value="high">High</option>
                <option value="medium">Medium</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-700">Title *</label>
              <Input
                data-testid="rule-form-title"
                value={ruleForm.title}
                onChange={(e) => setRuleForm(v => ({ ...v, title: e.target.value }))}
                placeholder="S3 bucket allows public read access"
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-700">Description</label>
              <textarea
                data-testid="rule-form-description"
                className="w-full border rounded px-2 py-2 text-sm h-24"
                value={ruleForm.description}
                onChange={(e) => setRuleForm(v => ({ ...v, description: e.target.value }))}
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Cloud Provider</label>
              <select
                data-testid="rule-form-provider"
                className="w-full border rounded px-2 py-2 text-sm"
                value={ruleForm.cloud_provider}
                onChange={(e) => setRuleForm(v => ({ ...v, cloud_provider: e.target.value }))}
              >
                <option value="aws">AWS</option>
                <option value="azure">Azure</option>
                <option value="gcp">GCP</option>
                <option value="all">All</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Category</label>
              <Input
                data-testid="rule-form-category"
                value={ruleForm.category}
                onChange={(e) => setRuleForm(v => ({ ...v, category: e.target.value }))}
                placeholder="s3, iam, etc."
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Resource Type</label>
              <Input
                data-testid="rule-form-resource-type"
                value={ruleForm.resource_type}
                onChange={(e) => setRuleForm(v => ({ ...v, resource_type: e.target.value }))}
                placeholder="AWS::S3::Bucket"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-gray-700">Active</label>
              <select
                data-testid="rule-form-active"
                className="w-full border rounded px-2 py-2 text-sm"
                value={ruleForm.is_active ? 'true' : 'false'}
                onChange={(e) => setRuleForm(v => ({ ...v, is_active: e.target.value === 'true' }))}
              >
                <option value="true">Active</option>
                <option value="false">Disabled</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-700">Remediation</label>
              <textarea
                data-testid="rule-form-remediation"
                className="w-full border rounded px-2 py-2 text-sm h-20"
                value={ruleForm.remediation}
                onChange={(e) => setRuleForm(v => ({ ...v, remediation: e.target.value }))}
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-700">References (one per line)</label>
              <textarea
                data-testid="rule-form-references"
                className="w-full border rounded px-2 py-2 text-sm h-16 font-mono text-xs"
                value={ruleForm.references_text}
                onChange={(e) => setRuleForm(v => ({ ...v, references_text: e.target.value }))}
              />
            </div>
            <div className="col-span-2">
              <label className="text-xs font-medium text-gray-700">
                Framework Mappings (comma-separated as &quot;Framework:ControlId&quot;, e.g. &quot;CIS:1.1, NIST:AC-3&quot;)
              </label>
              <Input
                data-testid="rule-form-frameworks"
                value={ruleForm.framework_ids_text}
                onChange={(e) => setRuleForm(v => ({ ...v, framework_ids_text: e.target.value }))}
                placeholder="CIS:1.1, NIST:AC-3, SOC2:CC6.1"
              />
            </div>
          </div>
          <DialogFooter className="mt-4">
            <Button variant="outline" onClick={() => setShowRuleDialog(false)}>Cancel</Button>
            <Button onClick={saveRule} data-testid="rule-form-save-btn">
              {editingRule ? 'Save Changes' : 'Create Rule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Update Detail Modal */}
      <Dialog open={!!selectedUpdate} onOpenChange={() => setSelectedUpdate(null)}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          {selectedUpdate && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  {getUpdateTypeIcon(selectedUpdate.update_type)}
                  {selectedUpdate.title}
                </DialogTitle>
                <DialogDescription>
                  Review and approve or reject this update
                </DialogDescription>
              </DialogHeader>
              
              <div className="space-y-4 mt-4">
                {/* Summary */}
                <div className="grid grid-cols-3 gap-4">
                  <div className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm text-gray-500">Total Records</p>
                    <p className="text-xl font-bold">{selectedUpdate.records_count}</p>
                  </div>
                  <div className="p-3 bg-green-50 rounded-lg">
                    <p className="text-sm text-gray-500">New Records</p>
                    <p className="text-xl font-bold text-green-600">{selectedUpdate.new_records}</p>
                  </div>
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm text-gray-500">Updated Records</p>
                    <p className="text-xl font-bold text-blue-600">{selectedUpdate.updated_records}</p>
                  </div>
                </div>
                
                {/* Severity Breakdown */}
                {selectedUpdate.summary?.severity_breakdown && (
                  <div className="p-4 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium mb-2">Severity Breakdown</p>
                    <div className="flex gap-4">
                      {Object.entries(selectedUpdate.summary.severity_breakdown).map(([sev, count]) => (
                        <div key={sev} className="flex items-center gap-2">
                          <Badge className={getSeverityColor(sev)}>{sev}</Badge>
                          <span className="font-bold">{count}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Records Preview */}
                <div className="border rounded-lg overflow-hidden">
                  <div className="bg-gray-100 p-3 border-b">
                    <p className="font-medium">Records Preview (first 10)</p>
                  </div>
                  <div className="max-h-60 overflow-y-auto">
                    {selectedUpdate.records?.slice(0, 10).map((record, idx) => (
                      <div key={idx} className="p-3 border-b last:border-b-0 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-sm">
                            {record.cve_id || record.rule_id || record.advisory_id}
                          </span>
                          <Badge className={getSeverityColor(record.severity)}>
                            {record.severity}
                          </Badge>
                        </div>
                        <p className="text-sm text-gray-600 mt-1">{record.title}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              
              <DialogFooter className="mt-6">
                <Button variant="outline" onClick={() => setSelectedUpdate(null)}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive"
                  onClick={() => setShowRejectDialog(true)}
                >
                  <X className="w-4 h-4 mr-2" />
                  Reject
                </Button>
                <Button 
                  className="bg-green-600 hover:bg-green-700"
                  onClick={() => approveUpdate(selectedUpdate.id)}
                  data-testid="approve-update-btn"
                >
                  <Check className="w-4 h-4 mr-2" />
                  Approve & Apply
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Update</DialogTitle>
            <DialogDescription>
              Please provide a reason for rejecting this update.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Input
              placeholder="Rejection reason (minimum 10 characters)"
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              onClick={() => selectedUpdate && rejectUpdate(selectedUpdate.id)}
            >
              Confirm Rejection
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default VulnerabilityDBAdminPage;
